
import Vue from 'vue';
import _debounce from 'lodash/debounce';
import { mapActions, mapState } from 'vuex';
import {
	UserRunnitNodeDefInfo,
	UserWorkflowRunnitInfo,
} from '@run-diffusion/shared';
import {
	SEARCH_IMAGE_ALLOWED_MIME_TYPES,
	SEARCH_IMAGE_MAX_BYTES,
	SEARCH_IMAGE_MAX_MEGAPIXELS,
	SearchGroup,
	SearchItem,
	SearchRequest,
	SearchResponse,
} from '@/types/search';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { storage } from '@/firebase';
import { analytics, EVENTS } from '@/services/analytics';
import { searchAll } from '@/services/searchApi';
import {
	addGlobalSearchRecent,
	getGlobalSearchRecents,
} from '@/utils/localStorage';
import DialogContent from '@/components/base/DialogContent.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import SearchRowBody from '@/components/GlobalSearch/SearchRowBody.vue';
import {
	RoutableSearchDestination,
	resolveSearchDestination,
} from '@/components/GlobalSearch/searchRouting';

const MIN_QUERY_LENGTH = 2;
// Grouped view shows this many rows per category; "View all" reveals the rest
// of the (already-fetched) category client-side. No server pagination.
const COMPACT_PER_GROUP = 4;
const DEBOUNCE_MS = 300;

interface RowVM {
	key: string;
	flatIndex: number;
	source: string | null;
	item: SearchItem | null;
	// idle rows
	title: string;
	subtitle: string | null;
	badge: string;
	icon: string | null;
	idleDestination?: RoutableSearchDestination;
}

// A Jump back in row before it takes its place in the list.
type JumpBackInRow = Omit<RowVM, 'flatIndex' | 'source' | 'item'> & {
	lastOpenAt?: UserRunnitNodeDefInfo['lastOpenAt'];
};

const openedMillis = (row: JumpBackInRow): number => row.lastOpenAt?.toMillis?.() ?? 0;

export default Vue.extend({
	name: 'GlobalSearchModal',
	components: {
		DialogContent,
		DialogOrBottomSheet,
		SearchRowBody,
	},
	data () {
		return {
			EMPTY_SUGGESTIONS: ['character', 'video', 'board', 'consistent'],
			IMAGE_ACCEPT: SEARCH_IMAGE_ALLOWED_MIME_TYPES.join(','),
			SOURCE_LABELS: {
				library: 'LIBRARY',
				tools: 'TOOLS',
				boards: 'BOARDS',
				templates: 'TEMPLATES',
				articles: 'ARTICLES',
			} as Record<string, string>,
			compactPerGroup: COMPACT_PER_GROUP,
			query: '',
			// The active Query Image (see CONTEXT.md): ephemeral file-picker
			// bytes, sent raw as the multipart `image` part. Never persisted.
			queryImage: null as File | null,
			queryImageUrl: null as string | null, // object URL for the chip thumb
			groups: [] as SearchGroup[],
			sourceView: null as SearchGroup | null,
			loading: false,
			errorState: false,
			settledQuery: '',
			activeIndex: -1,
			requestSeq: 0,
			inflight: null as AbortController | null,
			recents: [] as string[],
			// Thumb resolution is a WATCHER side effect, never a render one —
			// pending/failed guards prevent re-fires (an in-render fetch loop
			// pegged the page in QA; see the flatRows watcher below).
			thumbUrlCache: {} as Record<string, string>,
			thumbPending: {} as Record<string, true>,
			thumbFailed: {} as Record<string, true>,
			debouncedServerSearch: null as ReturnType<typeof _debounce> | null,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
			'recentUserRunnitNodeDefInfos',
			'recentUserWorkflowRunnitInfos',
		]),
		isOpen (): boolean {
			return !!(this.runnitState && this.runnitState.globalSearchOpen);
		},
		teamId (): string | null {
			const isTeam: boolean = this.runnitState?.runnitsOwnerSelection === 'TEAM' && !!this.team;
			return isTeam ? this.team.id : null;
		},
		accountId (): string {
			return this.teamId ?? 'personal'; // localStorage recents key (personal is a real key)
		},
		showIdle (): boolean {
			return !this.queryImage && this.query.trim().length < MIN_QUERY_LENGTH;
		},
		nonEmptyGroups (): SearchGroup[] {
			return (this.groups || []).filter((g: SearchGroup) => g.items.length > 0);
		},
		nonEmptyGroupCount (): number {
			return this.nonEmptyGroups.length;
		},
		hasAnyResults (): boolean {
			if (this.sourceView) return this.sourceView.items.length > 0;
			return this.nonEmptyGroups.length > 0;
		},
		totalDisplayedLabel (): string {
			const groups: SearchGroup[] = this.sourceView ? [this.sourceView] : this.nonEmptyGroups;
			const total: number = groups.reduce((sum, g) => sum + g.items.length, 0);
			const hasMore: boolean = groups.some((g) => g.hasMore);
			return `${total}${hasMore ? '+' : ''} result${total === 1 && !hasMore ? '' : 's'}`;
		},
		jumpBackInRows (): RowVM[] {
			const toolRows: JumpBackInRow[] = (this.recentUserRunnitNodeDefInfos || [])
				.filter((info: UserRunnitNodeDefInfo) => info && info.nodeDef)
				.map((info: UserRunnitNodeDefInfo) => ({
					key: `jump-tool-${info.nodeDefId}`,
					lastOpenAt: info.lastOpenAt,
					title: info.nodeDef.title,
					subtitle: info.nodeDef.description || null,
					badge: 'TOOL',
					icon: 'mdi-tools',
					idleDestination: { kind: 'TOOL', nativeIds: { nodeDefId: info.nodeDefId } },
				}));
			const boardRows: JumpBackInRow[] = (this.recentUserWorkflowRunnitInfos || [])
				.filter((info: UserWorkflowRunnitInfo) => info && info.runnit)
				.map((info: UserWorkflowRunnitInfo) => ({
					key: `jump-board-${info.runnitId}`,
					lastOpenAt: info.lastOpenAt,
					title: info.runnit.title,
					subtitle: info.runnit.accessLevel === 'SHARED' ? 'Shared' : 'Personal',
					badge: info.runnit.type === 'TEMPLATE' ? 'TEMPLATE' : 'BOARD',
					icon: 'mdi-view-dashboard-outline',
					idleDestination: info.runnit.type === 'TEMPLATE'
						? { kind: 'TEMPLATE', nativeIds: { templateId: info.runnitId } }
						: { kind: 'BOARD', nativeIds: { runnitId: info.runnitId } },
				}));
			return [...toolRows, ...boardRows]
				.sort((a: JumpBackInRow, b: JumpBackInRow) => openedMillis(b) - openedMillis(a))
				.slice(0, 6)
				.map((row: JumpBackInRow, i: number): RowVM => ({ ...row, flatIndex: i, source: null, item: null }));
		},
		flatRows (): RowVM[] {
			if (this.showIdle) return this.jumpBackInRows;
			if (this.sourceView) {
				const view: SearchGroup = this.sourceView;
				return view.items.map((item: SearchItem, i: number) =>
					this.itemToRow(item, view.source, i));
			}
			const rows: RowVM[] = [];
			for (const group of this.nonEmptyGroups) {
				for (const item of group.items.slice(0, COMPACT_PER_GROUP)) {
					rows.push(this.itemToRow(item, group.source, rows.length));
				}
			}
			return rows;
		},
	},
	watch: {
		flatRows: {
			immediate: true,
			handler () {
				this.resolveVisibleThumbs();
			},
		},
		isOpen: {
			// immediate is LOAD-BEARING: App.vue mounts this component with
			// v-if on the open flag, so a fresh instance is born with isOpen
			// already true and a plain watcher never fires — no focus, no
			// recents, no open analytics. immediate runs the handler on that
			// birth; focusSearchInput's rAF retry covers the not-yet-rendered
			// dialog content.
			immediate: true,
			handler (open: boolean) {
				if (open) {
					this.recents = this.user
						? getGlobalSearchRecents(this.user.id, this.accountId)
						: [];
					this.activeIndex = -1;
					this.focusSearchInput();
					analytics.track(EVENTS.SEARCH_OPENED, {
						trigger: this.runnitState?.globalSearchTrigger || 'button',
					});
				} else {
					this.abortInflight();
				}
			},
		},
		accountId () {
			// Account switch: cancel, clear results, reload recents (per plan).
			this.abortInflight();
			this.groups = [];
			this.sourceView = null;
			this.errorState = false;
			this.recents = this.user
				? getGlobalSearchRecents(this.user.id, this.accountId)
				: [];
			if (!this.showIdle) this.runSearch(true);
		},
	},
	created () {
		this.debouncedServerSearch = _debounce(() => this.runSearch(false), DEBOUNCE_MS);
	},
	beforeDestroy () {
		this.abortInflight();
		// abortInflight only stops a request already issued. A keystroke within
		// DEBOUNCE_MS of closing leaves the timer armed, and it would fire
		// runSearch on a destroyed instance: a token fetch, a device
		// registration, and a POST whose response nobody reads.
		this.debouncedServerSearch?.cancel();
		if (this.queryImageUrl) URL.revokeObjectURL(this.queryImageUrl);
	},
	methods: {
		...mapActions(['updateRunnitState', 'updateSnackbar']),
		onDialogInput (open: boolean) {
			if (!open) this.close();
		},
		close () {
			this.updateRunnitState({ globalSearchOpen: false, globalSearchTrigger: null });
		},
		focusSearchInput () {
			// Vuetify's dialog focuses its OWN content wrapper on open (a
			// double-nextTick after show), so a single early focus() gets
			// robbed — re-assert across the whole open window. Two clocks
			// drive the SAME idempotent grab: rAF for frame-accurate
			// re-assertion in foreground tabs, and fixed timers because rAF
			// is frozen entirely in throttled tabs (background/automation) —
			// verified live: an rAF-only loop never ran there. Re-focusing an
			// already-focused element is a no-op, so once the caret lands and
			// the user types, later grabs change nothing.
			const grab = () => {
				if (!this.isOpen) return;
				const field = this.$refs.searchInput as Vue | undefined;
				const el: HTMLInputElement | null | undefined = field?.$el.querySelector('input');
				if (el && el.isConnected && document.activeElement !== el) {
					el.focus();
				}
			};
			for (const delayMs of [0, 50, 150, 350, 700]) {
				setTimeout(grab, delayMs);
			}
			let frames: number = 45;
			const loop = () => {
				grab();
				if (--frames > 0) requestAnimationFrame(loop);
			};
			requestAnimationFrame(loop);
		},
		clearQuery () {
			this.query = '';
			this.sourceView = null;
			this.groups = [];
			this.errorState = false;
			this.activeIndex = -1;
			// Both of these matter, and clearQueryImage already does them.
			// A pending debounce would search for the text we just cleared, and
			// leaving settledQuery set makes retyping the SAME term a no-op:
			// runSearch reads `q === settledQuery` as already answered, but the
			// groups that answered it were just cleared.
			this.abortInflight();
			this.debouncedServerSearch?.cancel();
			this.settledQuery = '';
			this.focusSearchInput();
		},
		openImagePicker () {
			const el = this.$refs.imageFileInput as HTMLInputElement | undefined;
			if (el) el.click();
		},
		async onImageFilePicked (event: Event) {
			const input = event.target as HTMLInputElement;
			const file: File | null = (input.files && input.files[0]) || null;
			input.value = ''; // allow re-picking the same file later
			if (!file) return;
			const error: string | null = await this.validateQueryImage(file);
			if (error) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: error,
					show: true,
				});
				return;
			}
			// Enter image mode: the Query Image replaces the text query wholesale.
			this.query = '';
			this.settledQuery = '';
			this.sourceView = null;
			this.queryImage = file;
			this.queryImageUrl = URL.createObjectURL(file);
			this.runSearch(true);
			this.focusRoot();
		},
		// The server's intake limits, checked here first so a bad file never
		// leaves the browser (see types/search.ts); the server checks again.
		async validateQueryImage (file: File): Promise<string | null> {
			if (!SEARCH_IMAGE_ALLOWED_MIME_TYPES.includes(file.type)) {
				return 'Search images must be PNG, JPEG, or WEBP';
			}
			if (file.size > SEARCH_IMAGE_MAX_BYTES) {
				return `Search images must be under ${Math.floor(SEARCH_IMAGE_MAX_BYTES / (1024 * 1024))} MB`;
			}
			const dims = await new Promise<{ width: number, height: number } | null>((resolve) => {
				const url: string = URL.createObjectURL(file);
				const img = new Image();
				img.onload = () => {
					resolve({ width: img.naturalWidth, height: img.naturalHeight });
					URL.revokeObjectURL(url);
				};
				img.onerror = () => {
					resolve(null);
					URL.revokeObjectURL(url);
				};
				img.src = url;
			});
			if (!dims) return 'We couldn\'t read that image file';
			if ((dims.width * dims.height) / 1000000 > SEARCH_IMAGE_MAX_MEGAPIXELS) {
				return `Search images must be under ${SEARCH_IMAGE_MAX_MEGAPIXELS} megapixels`;
			}
			return null;
		},
		clearQueryImage () {
			if (this.queryImageUrl) URL.revokeObjectURL(this.queryImageUrl);
			this.queryImage = null;
			this.queryImageUrl = null;
			this.abortInflight();
			this.groups = [];
			this.sourceView = null;
			this.errorState = false;
			this.settledQuery = '';
			this.activeIndex = -1;
			this.focusSearchInput();
		},
		focusRoot () {
			this.$nextTick(() => {
				const el = this.$el as HTMLElement | undefined;
				if (el && el.focus) el.focus();
			});
		},
		onRootKeydown (event: KeyboardEvent) {
			// The text input has its own keydown handler; this root fallback
			// exists for image mode, where no input is rendered to catch keys.
			if (!this.queryImage) return;
			if (event.target instanceof HTMLInputElement) return;
			this.onInputKeydown(event);
		},
		abortInflight () {
			if (this.inflight) {
				this.inflight.abort();
				this.inflight = null;
			}
			this.loading = false;
		},
		onQueryInput (value: string) {
			this.query = value;
			this.sourceView = null;
			this.errorState = false;
			if (this.showIdle) {
				this.abortInflight();
				this.groups = [];
				this.activeIndex = -1;
				return;
			}
			this.loading = true;
			this.debouncedServerSearch?.();
		},
		async runSearch (force: boolean) {
			// Image mode and text mode are mutually exclusive in v1: the request
			// carries exactly one of Query Image or query text.
			const q: string = this.queryImage ? '' : this.query.trim();
			if (!this.queryImage && q.length < MIN_QUERY_LENGTH) return;
			if (!force && !this.queryImage && q === this.settledQuery && !this.errorState) {
				// Typing back to the settled query (a trailing space, a typo and
				// its backspace): the results on screen already answer it. Clear
				// the loading flag onQueryInput raised, and drop any request
				// still in flight for the in-between text.
				this.abortInflight();
				return;
			}
			const request: SearchRequest = this.queryImage
				? { image: this.queryImage }
				: { query: q };
			const seq: number = ++this.requestSeq;
			this.abortInflight();
			const controller = new AbortController();
			this.inflight = controller;
			this.loading = true;
			this.errorState = false;
			const startedAt: number = Date.now();
			try {
				const response: SearchResponse = await searchAll(
					request, this.teamId, controller.signal,
				);
				if (seq !== this.requestSeq) return; // stale
				this.groups = response.groups || [];
				this.settledQuery = q;
				this.activeIndex = -1;
				const counts: Record<string, number> = {};
				for (const g of this.groups) counts[g.source] = g.items.length;
				analytics.track(EVENTS.SEARCH_PERFORMED, {
					query_mode: this.queryImage ? 'image' : 'text',
					query_length: q.length,
					category_counts: counts,
					has_results: this.hasAnyResults,
					latency_ms: Date.now() - startedAt,
				});
				if (!this.hasAnyResults) {
					analytics.track(EVENTS.SEARCH_ZERO_RESULTS, {
						query_mode: this.queryImage ? 'image' : 'text',
						query_length: q.length,
					});
				}
			} catch (error: unknown) {
				if ((error as { name?: string } | null)?.name === 'AbortError') return;
				if (seq !== this.requestSeq) return;
				console.error('Global search failed', error);
				this.groups = [];
				this.errorState = true;
			} finally {
				if (seq === this.requestSeq) this.loading = false;
			}
		},
		viewAll (source: string) {
			analytics.track(EVENTS.SEARCH_VIEW_ALL_CLICKED, { category: source });
			// Client-side reveal: the full (capped) category is already in
			// `groups` — no server round trip, no pagination.
			this.sourceView = this.groups.find((g: SearchGroup) => g.source === source) || null;
			this.activeIndex = -1;
		},
		exitSourceView () {
			this.sourceView = null;
			this.activeIndex = -1;
		},
		itemToRow (item: SearchItem, source: string, flatIndex: number): RowVM {
			return {
				key: item.id,
				flatIndex,
				source,
				item,
				title: item.title,
				subtitle: item.subtitle || null,
				badge: item.type.toUpperCase(),
				icon: null,
			};
		},
		rowsForGroup (group: SearchGroup): RowVM[] {
			return this.flatRows.filter((r: RowVM) => r.source === group.source);
		},
		onRecentClick (recent: string, rank: number) {
			analytics.track(EVENTS.SEARCH_RECENT_CLICKED, { rank });
			this.onChipQuery(recent);
		},
		// Chip clicks (recent searches, empty-state suggestions) run the query
		// AND return focus to the input — a chip is a <button>, so without this
		// keyboard navigation stalls until the user clicks back into the field.
		onChipQuery (query: string) {
			this.onQueryInput(query);
			this.runSearch(true);
			this.focusSearchInput();
		},
		saveRecent () {
			const q: string = this.query.trim();
			if (this.user && q.length >= MIN_QUERY_LENGTH) {
				this.recents = addGlobalSearchRecent(this.user.id, this.accountId, q);
			}
		},
		// Arrow-key navigation over flatRows — the JUMP BACK IN rows while idle,
		// the result rows otherwise. Nothing is highlighted until the first arrow
		// press, which lands on the first row regardless of direction; from there
		// movement wraps: past the bottom returns to the top, before the top jumps
		// to the bottom.
		moveActive (delta: number) {
			const rows: RowVM[] = this.flatRows;
			if (!rows.length) {
				this.activeIndex = -1;
				return;
			}
			this.activeIndex = this.activeIndex < 0
				? 0
				: (this.activeIndex + delta + rows.length) % rows.length;
			this.scrollActiveIntoView();
		},
		onInputKeydown (event: KeyboardEvent) {
			const rows: RowVM[] = this.flatRows;
			if (event.key === 'Escape') {
				event.preventDefault();
				this.close();
			} else if (event.key === 'ArrowDown') {
				event.preventDefault();
				this.moveActive(1);
			} else if (event.key === 'ArrowUp') {
				event.preventDefault();
				this.moveActive(-1);
			} else if (event.key === 'Home' && rows.length) {
				event.preventDefault();
				this.activeIndex = 0;
				this.scrollActiveIntoView();
			} else if (event.key === 'End' && rows.length) {
				event.preventDefault();
				this.activeIndex = rows.length - 1;
				this.scrollActiveIntoView();
			} else if (event.key === 'Enter') {
				event.preventDefault();
				if (this.showIdle && this.activeIndex < 0) return;
				if (this.activeIndex >= 0 && rows[this.activeIndex]) {
					this.openRow(rows[this.activeIndex], event.metaKey || event.ctrlKey);
				} else {
					this.saveRecent();
					this.runSearch(true);
				}
			}
		},
		scrollActiveIntoView () {
			this.$nextTick(() => {
				const el = document.getElementById(`gs-row-${this.activeIndex}`);
				if (el) el.scrollIntoView({ block: 'nearest' });
			});
		},
		openRow (row: RowVM, secondary: boolean = false) {
			let destination: RoutableSearchDestination | null = null;
			if (row.item) {
				destination = secondary && row.item.destinations.length > 1
					? row.item.destinations[1]
					: row.item.destinations[0];
				analytics.track(EVENTS.SEARCH_RESULT_CLICKED, {
					category: row.source,
					entity_type: row.item.type,
					rank: row.flatIndex,
					via: secondary ? 'secondary' : 'primary',
				});
				this.saveRecent();
			} else if (row.idleDestination) {
				destination = row.idleDestination;
			}
			if (!destination) return;
			this.openDestination(destination);
		},
		openDestination (destination: RoutableSearchDestination) {
			const resolved = resolveSearchDestination(destination);
			if (!resolved) return;
			this.close();
			if (resolved.externalUrl) {
				window.open(resolved.externalUrl, '_blank', 'noopener');
			} else if (resolved.location) {
				this.routerPush(this.$route, this.$router, resolved.location);
			}
		},
		// PURE render-path lookup — never triggers fetches (see resolveVisibleThumbs).
		thumbUrlFor (row: RowVM): string | null {
			const thumb = row.item?.thumbnail;
			if (!thumb) return null;
			if (thumb.url) return thumb.url;
			return (thumb.storagePath && this.thumbUrlCache[thumb.storagePath]) || null;
		},
		resolveVisibleThumbs (): void {
			for (const row of this.flatRows as RowVM[]) {
				const path: string | undefined = row.item?.thumbnail?.storagePath;
				if (
					!path
					|| this.thumbUrlCache[path]
					|| this.thumbPending[path]
					|| this.thumbFailed[path]
				) continue;
				this.$set(this.thumbPending, path, true);
				storage.ref(path).getDownloadURL()
					.then((url: string) => this.$set(this.thumbUrlCache, path, url))
					.catch(() => this.$set(this.thumbFailed, path, true))
					.finally(() => this.$delete(this.thumbPending, path));
			}
		},
	},
});
